import { useMemo } from 'react';
import { generateDropdownOptions } from 'globalutils/fmUtils';
import { trackingDeviceList } from './config';
import { checkServiceProvider } from './serviceProviderConfig';

export default function useInitialValues({
  vehicle,
  regions,
  type,
  driverList,
  hardwareDeviceList,
  mechanicList,
  vehicleTypes,
  fuelTypes,
  cameraTypes,
  trailerList,
  years,
  checkCameraFeature,
  objectArrayFromDiffFields,
}) {
  const defaultYear = () => {
    const currentYear = new Date().getFullYear();
    const defaultYear = years.find((o) => o.value === currentYear);
    return defaultYear || years[0];
  };

  const { terminalList, departmentList, subdepartmentList, inspectionForms } = useMemo(() => {
    const terminalList = generateDropdownOptions(
      vehicle?.terminals?.length ? vehicle.terminals : [],
      'terminalName',
      'terminalId'
    );
    const departmentList = generateDropdownOptions(
      vehicle?.departments?.length ? vehicle.departments : [],
      'departmentName',
      'departmentId'
    );
    const subdepartmentList = generateDropdownOptions(
      vehicle?.subDepartments?.length ? vehicle.subDepartments : [],
      'subDepartmentName',
      'subDepartmentId'
    );

    const inspectionForms = objectArrayFromDiffFields(
      vehicle?.customDvirForms?.filter((ele) => ele.unitType == 'VEHICLE'),
      'idPub',
      'name'
    );

    return {
      terminalList,
      departmentList,
      subdepartmentList,
      inspectionForms,
    };
  }, [vehicle]);

  const initialValues = useMemo(() => {
    const drivers =
      vehicle?.vehicleDetails?.driversIds && type !== 'add' && driverList
        ? driverList.filter((driver) => vehicle.vehicleDetails.driversIds.includes(driver.value))
        : [];

    return {
      // intialValues set conditionally for the add and edit screen using scope operator at begins and end of obj change entire instead of this fix in future
      ...(type == !'add'
        ? {
            deviceType: 'NONE',
            camera: 'No',
          }
        : {}),
      companyId: vehicle?.vehicleDetails?.companyId || '',
      deviceType: vehicle?.vehicleDetails?.deviceType || 'NONE',
      camera: vehicle?.vehicleDetails?.cameraSerialNo ? 'Yes' : 'No',
      driversIds: drivers,
      esn: vehicle?.vehicleDetails?.esn || '',
      gvw: vehicle?.vehicleDetails?.gvw || '',
      trackingDevice: vehicle?.vehicleDetails?.trackingDevice
        ? trackingDeviceList.filter((ele) => ele.value === vehicle?.vehicleDetails?.trackingDevice)[0]
        : '',
      hardwareDeviceKey: vehicle?.vehicleDetails?.hardwareDeviceKey
        ? hardwareDeviceList.filter((hd) => hd.value === vehicle?.vehicleDetails?.hardwareDeviceKey)[0]
        : '',
      company: vehicle?.company
        ? {
            label: vehicle.company.name,
            value: vehicle.company.id,
          }
        : '',
      deviceTypeError: '',
      hardwareDeviceKeyError: '',
      ibeaconRequired: vehicle?.vehicleDetails?.ibeaconRequired || '',
      ibeaconUuid: vehicle?.vehicleDetails?.ibeaconUuid || '',
      cameraIntegrationEnabled: vehicle?.cameraIntegrationEnabled || false,
      ...checkServiceProvider('initialval', window.$environment?.SERVICE_PROVIDER, false),
      make: vehicle?.vehicleDetails?.make || '',
      mechanicId: mechanicList.filter((mech) => mech.value === vehicle?.vehicleDetails?.mechanicId)[0] || '',
      model: vehicle?.vehicleDetails?.model || '',
      webFleetUid: vehicle?.vehicleDetails?.webFleetUid || '',
      name: vehicle?.vehicleDetails?.name || '',
      nextMaintenanceMileage: vehicle?.vehicleDetails?.nextMaintenanceMileage || '',
      next_maintenance_date: vehicle?.vehicleDetails?.next_maintenance_date || '',
      currentOdometer: vehicle?.vehicleDetails?.odometer || '',
      ...(checkCameraFeature()
        ? {
            cameraType: vehicle?.vehicleDetails?.cameraType
              ? cameraTypes?.find((e) => e?.value === vehicle?.vehicleDetails?.cameraType)
              : cameraTypes[0],
            cameraSerialNo: vehicle?.vehicleDetails?.cameraSerialNo || '',
            cameraPassword: vehicle?.vehicleDetails?.cameraPassword || '',
          }
        : {}),
      plateNumber: vehicle?.vehicleDetails?.plateNumber || '',
      garagingAddress: vehicle?.vehicleDetails?.garagingAddress || '',
      radiusCompliance: vehicle?.vehicleDetails?.radiusCompliance
        ? {
            label: vehicle?.vehicleDetails?.radiusCompliance,
            value: vehicle?.vehicleDetails?.radiusCompliance,
          }
        : '',
      garagingState: vehicle?.vehicleDetails?.garagingState
        ? {
            label: vehicle?.vehicleDetails?.garagingState,
            value: vehicle?.vehicleDetails?.garagingState,
          }
        : '',
      garagingCity: vehicle?.vehicleDetails?.garagingCity || '',
      garagingZipCode: vehicle?.vehicleDetails?.garagingZipCode || '',
      regionId: (regions && regions.length && regions.filter((reg) => reg.value === vehicle?.vehicleDetails?.regionId)[0]) || '',
      terminalId:
        (terminalList &&
          terminalList.length &&
          terminalList.filter((ter) => ter.value === vehicle?.vehicleDetails?.terminalId)[0]) ||
        '',
      departmentId:
        (departmentList &&
          departmentList.length &&
          departmentList.filter((dep) => dep.value === vehicle?.vehicleDetails?.departmentId)[0]) ||
        '',
      subDepartmentId:
        (subdepartmentList &&
          subdepartmentList.length &&
          subdepartmentList.filter((sub) => sub.value === vehicle?.vehicleDetails?.subDepartmentId)[0]) ||
        '',
      trailerIdPubs: trailerList.filter((trailer) => vehicle?.vehicleDetails?.trailerIdPubs?.includes(trailer.value)) || [],
      type: vehicle?.vehicleDetails?.type
        ? {
            label: vehicle?.vehicleDetails?.type,
            value: vehicle?.vehicleDetails?.type,
          }
        : vehicleTypes[0],
      fuelType: vehicle?.vehicleDetails?.fuelType
        ? {
            label: vehicle?.vehicleDetails?.fuelType,
            value: vehicle?.vehicleDetails?.fuelType,
          }
        : fuelTypes[0],
      vehicleState: vehicle?.vehicleDetails?.vehicleState || '',
      vin: vehicle?.vehicleDetails?.vin || '',
      year: vehicle?.vehicleDetails?.year
        ? {
            label: vehicle?.vehicleDetails?.year,
            value: vehicle?.vehicleDetails?.year,
          }
        : defaultYear(),
      customDvirFormId:
        (vehicle?.vehicleDetails?.customDvirFormId &&
          inspectionForms.filter((form) => form.value === vehicle?.vehicleDetails?.customDvirFormId)[0]) ||
        '',
      ...(type === 'add'
        ? {
            deviceType: 'NONE',
            camera: 'No',
          }
        : {}),
      idPub: vehicle?.vehicleDetails?.idPub,
      wlId: vehicle?.vehicleDetails?.wlId || '',
      isEdit: vehicle?.vehicleDetails?.isEdit,
    };
  }, [vehicle]);

  return { initialValues };
}
